import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { templatesApi } from '../../../utils/templatesApi';
import './TemplateGallery.css';

/**
 * The shared template picker (BBD-106). Sections, in order: Recently used,
 * This workspace (featured first; workspace context only), My templates,
 * Bluebird presets grouped by category. Search, arrow-key navigation, Enter
 * to choose, Esc to close, and a live preview rendered by the server with
 * sample values (kits show their page tree).
 *
 * Props:
 *   isOpen, onClose
 *   onChoose(template)   – the chosen template object (has .ref, .kind, .fields …)
 *   workspaceId          – show the workspace section and render {{workspace}}
 *   kind                 – restrict to one kind, e.g. "snippet" (BBD-108)
 *   title, chooseLabel   – copy overrides
 *   compact              – smaller modal (snippet picker)
 */
const TemplateGallery = ({
    isOpen,
    onClose,
    onChoose,
    workspaceId,
    kind,
    title = 'Start from a template',
    chooseLabel = 'Use template',
    compact = false,
}) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [query, setQuery] = useState('');
    const [activeRef, setActiveRef] = useState(null);
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const listRef = useRef(null);
    const searchRef = useRef(null);
    const previewTimer = useRef(null);
    const previewSeq = useRef(0);

    // Load the gallery each time it opens (templates may have changed).
    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        setLoading(true);
        setError('');
        setQuery('');
        setPreview(null);
        templatesApi.gallery({ workspaceId, kind })
            .then((d) => { if (!cancelled) setData(d); })
            .catch((e) => { if (!cancelled) setError(e.message || 'Could not load templates'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        setTimeout(() => searchRef.current?.focus(), 30);
        return () => { cancelled = true; };
    }, [isOpen, workspaceId, kind]);

    const sections = useMemo(() => {
        if (!data) return [];
        const q = query.trim().toLowerCase();
        const matches = (t) => !q || [t.name, t.description, t.category].some((s) => (s || '').toLowerCase().includes(q));
        const out = [];
        const recent = (data.recent || []).filter(matches);
        if (recent.length) out.push({ key: 'recent', title: 'Recently used', items: recent });
        if (workspaceId) {
            const ws = (data.workspace || []).filter(matches);
            if (ws.length) out.push({ key: 'workspace', title: 'This workspace', items: ws });
        }
        const mine = (data.personal || []).filter(matches);
        if (mine.length) out.push({ key: 'personal', title: 'My templates', items: mine });
        const byCategory = new Map();
        (data.presets || []).filter(matches).forEach((p) => {
            const cat = p.category || 'Bluebird presets';
            if (!byCategory.has(cat)) byCategory.set(cat, []);
            byCategory.get(cat).push(p);
        });
        (data.categories || []).forEach((cat) => {
            if (byCategory.has(cat)) out.push({ key: `preset-${cat}`, title: cat, items: byCategory.get(cat), preset: true });
        });
        byCategory.forEach((items, cat) => {
            if (!(data.categories || []).includes(cat)) out.push({ key: `preset-${cat}`, title: cat, items, preset: true });
        });
        return out;
    }, [data, query, workspaceId]);

    // Flat list for keyboard navigation; a template can appear twice (recent + its home section).
    const flat = useMemo(() => sections.flatMap((s) => s.items.map((t) => ({ id: `${s.key}:${t.ref}`, template: t }))), [sections]);

    useEffect(() => {
        if (!flat.length) { setActiveRef(null); return; }
        if (!activeRef || !flat.some((f) => f.id === activeRef)) setActiveRef(flat[0].id);
    }, [flat, activeRef]);

    const active = useMemo(() => flat.find((f) => f.id === activeRef)?.template || null, [flat, activeRef]);

    // Server-rendered preview with sample values (never counts as a use).
    useEffect(() => {
        clearTimeout(previewTimer.current);
        if (!active || !isOpen) { setPreview(null); return; }
        const seq = ++previewSeq.current;
        setPreviewLoading(true);
        previewTimer.current = setTimeout(() => {
            templatesApi.render({ ref: active.ref, workspaceId, preview: true })
                .then((r) => { if (seq === previewSeq.current) setPreview(r); })
                .catch(() => { if (seq === previewSeq.current) setPreview({ error: true }); })
                .finally(() => { if (seq === previewSeq.current) setPreviewLoading(false); });
        }, 150);
        return () => clearTimeout(previewTimer.current);
    }, [active, workspaceId, isOpen]);

    const choose = useCallback((t) => {
        if (t && onChoose) onChoose(t);
    }, [onChoose]);

    const onKeyDown = (e) => {
        if (e.key === 'Escape') { e.preventDefault(); onClose?.(); return; }
        if (!flat.length) return;
        const idx = Math.max(0, flat.findIndex((f) => f.id === activeRef));
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            const next = flat[Math.min(idx + 1, flat.length - 1)];
            setActiveRef(next.id);
            listRef.current?.querySelector(`[data-item="${CSS.escape(next.id)}"]`)?.scrollIntoView({ block: 'nearest' });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            const prev = flat[Math.max(idx - 1, 0)];
            setActiveRef(prev.id);
            listRef.current?.querySelector(`[data-item="${CSS.escape(prev.id)}"]`)?.scrollIntoView({ block: 'nearest' });
        } else if (e.key === 'Enter') {
            e.preventDefault();
            choose(active);
        }
    };

    if (!isOpen) return null;

    const tree = preview && preview.kind === 'kit' ? buildTree(preview) : null;

    return ReactDOM.createPortal(
        <div className="TplGallery__overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
            <div
                className={`TplGallery__modal${compact ? ' TplGallery__modal--compact' : ''}`}
                role="dialog"
                aria-modal="true"
                aria-label={title}
                onKeyDown={onKeyDown}
            >
                <div className="TplGallery__header">
                    <h3 className="TplGallery__title">{title}</h3>
                    <input
                        ref={searchRef}
                        className="TplGallery__search"
                        type="search"
                        placeholder="Search templates…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        aria-label="Search templates"
                    />
                    <button type="button" className="TplGallery__close" onClick={onClose} aria-label="Close">×</button>
                </div>
                {error && <div className="TplGallery__error">{error}</div>}
                <div className="TplGallery__body">
                    <div className="TplGallery__list" ref={listRef} tabIndex={-1}>
                        {loading && <div className="TplGallery__status">Loading templates…</div>}
                        {!loading && !sections.length && (
                            <div className="TplGallery__empty">
                                {query ? 'No templates match your search.' : 'No templates yet.'}
                            </div>
                        )}
                        {sections.map((s) => (
                            <div className="TplGallery__section" key={s.key}>
                                <div className="TplGallery__sectionTitle">{s.title}</div>
                                {s.items.map((t) => {
                                    const id = `${s.key}:${t.ref}`;
                                    return (
                                        <button
                                            type="button"
                                            key={id}
                                            data-item={id}
                                            className={`TplGallery__item${id === activeRef ? ' TplGallery__item--active' : ''}`}
                                            onMouseEnter={() => setActiveRef(id)}
                                            onFocus={() => setActiveRef(id)}
                                            onClick={() => setActiveRef(id)}
                                            onDoubleClick={() => choose(t)}
                                        >
                                            <span className="TplGallery__itemText">
                                                <span className="TplGallery__itemName">
                                                    {t.name}
                                                    {t.is_featured && <span className="TplGallery__badge TplGallery__badge--featured">Featured</span>}
                                                    {t.kind === 'kit' && <span className="TplGallery__badge">Kit</span>}
                                                    {t.kind === 'snippet' && !kind && <span className="TplGallery__badge">Snippet</span>}
                                                    {t.kind === 'space' && <span className="TplGallery__badge">Space</span>}
                                                </span>
                                                <span className="TplGallery__itemDesc">{t.description}</span>
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                    <div className="TplGallery__preview">
                        {active ? (
                            <>
                                <div className="TplGallery__previewHead">
                                    <h4 className="TplGallery__previewTitle">
                                        {active.name}
                                    </h4>
                                    <div className="TplGallery__previewMeta">
                                        {active.description}
                                        {active.scope === 'workspace' && active.owner_name ? ` · by ${active.owner_name}` : ''}
                                        {active.fields?.length ? ` · asks for ${active.fields.map((f) => f.label || f.key).join(', ')}` : ''}
                                    </div>
                                    {active.note && <div className="TplGallery__previewNote">{active.note}</div>}
                                </div>
                                <div className="TplGallery__previewBody">
                                    {previewLoading && !preview && <div className="TplGallery__status">Rendering preview…</div>}
                                    {preview?.error && <div className="TplGallery__status">Preview unavailable.</div>}
                                    {preview && !preview.error && (
                                        <>
                                            {preview.kind !== 'snippet' && (
                                                <div className="TplGallery__previewMeta" style={{ marginBottom: 8 }}>
                                                    Title: <strong>{preview.name}</strong>
                                                </div>
                                            )}
                                            {tree && (
                                                <ul className="TplGallery__tree" aria-label="Pages this kit creates">
                                                    {tree.map((n) => (
                                                        <li key={n.key} className={n.depth === 1 ? 'is-child' : n.depth === 2 ? 'is-grandchild' : ''}>
                                                            <i className={`fas ${n.depth === 0 ? 'fa-folder' : 'fa-file-lines'} TplGallery__treeIcon`} aria-hidden="true"></i>
                                                            {n.name}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                            <div
                                                className="TplGallery__previewDoc"
                                                // Server-rendered template HTML (presets or the user's/workspace's own templates).
                                                dangerouslySetInnerHTML={{ __html: preview.content || '<p><em>Empty template</em></p>' }}
                                            />
                                        </>
                                    )}
                                </div>
                            </>
                        ) : (
                            <div className="TplGallery__status">Pick a template to see a preview.</div>
                        )}
                        <div className="TplGallery__previewFoot">
                            <span className="TplGallery__hint">↑↓ to browse · Enter to choose · Esc to close</span>
                            <button type="button" className="TplGallery__btn" onClick={onClose}>Cancel</button>
                            <button
                                type="button"
                                className="TplGallery__btn TplGallery__btn--primary"
                                disabled={!active}
                                onClick={() => choose(active)}
                            >
                                {active?.kind === 'kit' ? 'Create kit' : chooseLabel}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
};

/** Flatten a rendered kit into [{key, name, depth}] for the preview tree. */
function buildTree(rendered) {
    const nodes = [{ key: '__space', name: rendered.name, depth: 0 }];
    const depthOf = { __space: 0 };
    (rendered.pages || []).forEach((p) => {
        const parentDepth = p.parent_key && depthOf[p.parent_key] !== undefined ? depthOf[p.parent_key] : 0;
        depthOf[p.key] = parentDepth + 1;
        nodes.push({ key: p.key, name: p.name, depth: Math.min(parentDepth + 1, 2) });
    });
    return nodes;
}

export default TemplateGallery;
